/**
 * Created by Ning Jiang on 3/12/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {WildNudgeSoundConfig} from "../../WildNudgeSoundConfig";

export class WildNudgeInitialWinCountUp extends BitmapWinCountUp {

    private _isCountUpSoundPlaying:boolean = false;
    private _hasCountUpEndSoundPlayed:boolean = false;

    constructor() {
        super({
            font : {
                name : "NumbersHotNudge",
                size : 46
            },
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
    }

    private getScale(multiplier:number):number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number):Timeline {
        const oriNumber:number = MathHelper.floorToDecimals(to / multiplier, 2);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.COUNT_UP);
                    this._isCountUpSoundPlaying = true;
                }
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power3.easeInOut})
        ]);

        timeline.add(() => {this.stopCountUpSound();});

        if(multiplier > 1) {
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m);
                timeline.add([
                    () => {
                        SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.MULTIPLIER);
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, 0.3,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, 0.3, {}));
            }
        }

        return timeline;
    }

    private stopCountUpSound():void {
        if(this._isCountUpSoundPlaying) {
            SlotGame.sound.stopEffect(WildNudgeSoundConfig.instance.COUNT_UP);
            this._isCountUpSoundPlaying = false;
        }

        if(!this._hasCountUpEndSoundPlayed) {
            SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.COUNT_UP_ENDS);
            this._hasCountUpEndSoundPlayed = true;
        }
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        return super.abort(endValue, isAborted, multiplier);
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(multiplier > 1) {
            const scale:number = this.getScale(multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x : scale,
                y : scale,
                ease : Elastic.easeOut.config(2, 1)
            }), 0)
        }
        return timeline;
    }

    public getEndingAnimation(endValue:number):Timeline {
        const timeline:TimelineLite = new TimelineLite({
            onComplete : () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
            }
        });
        timeline.add(
            () => {this.stopCountUpSound();}
        );
        timeline.add(TweenLite.to(this._countUp, 0.5, {alpha : 0}), `+=0.8`); // stay a while after count up.

        return timeline;
    }
}